import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createVNode as _createVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, withCtx as _withCtx, Transition as _Transition } from "vue";
import _imports_0 from '@/assets/L-Arrow-24px.svg';
const _hoisted_1 = { class: "stm-wrap" };
const _hoisted_2 = { class: "stm-meta" };
const _hoisted_3 = {
    key: 0,
    class: "stm-crumb"
};
const _hoisted_4 = { class: "stm-crumb-pill" };
const _hoisted_5 = { class: "stm-crumb-name" };
const _hoisted_6 = { class: "stm-meta-label" };
const _hoisted_7 = { class: "stm-meta-total" };
const _hoisted_8 = { class: "stm-tile-label-name" };
const _hoisted_9 = { class: "stm-tile-label-name-text" };
const _hoisted_10 = {
    key: 0,
    class: "stm-tile-label-value"
};
const _hoisted_11 = ["src"];
const _hoisted_12 = {
    key: 1,
    class: "stm-empty"
};
const _hoisted_13 = {
    key: 2,
    class: "stm-empty"
};
const _hoisted_14 = {
    key: 1,
    class: "stm-hint"
};
const _hoisted_15 = {
    key: 0,
    class: "stm-vouchers"
};
const _hoisted_16 = { class: "stm-drawer-tx" };
import { ref, computed, onMounted, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useDarkMode } from '@/composables/useDarkMode';
import { useBanking } from '@/composables/useBanking';
import { useVouchers } from '@/composables/useVouchers';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import MGToggle from './elements/MGToggle.vue';
import PeriodToggle from './elements/PeriodToggle.vue';
import RetailDiscountCard from './elements/RetailDiscountCard.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
import TransactionsList from './elements/TransactionsList.vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { TreemapChart } from 'echarts/charts';
import { getSpendingAnalysis, getTransactionClassificationTree, getMonthRangeForSelection, getDefaultMonthRange, groupTransactionsByDay, formatTimestampString, SPENDING_BREAKDOWN_PALETTE, } from '@/composables/useSpendingAnalysis';
import { selectedPeriod, selectedAccount, dateSelection, snapToSpendingCycle, } from './elements/spendingAnalysisFilterState';
import { consumePendingWriteRevalidate, inflightCategoryUpdate } from './elements/transactionUpdateState';
import { useTransactionUpdateHandoff } from '@/composables/useTransactionUpdateHandoff';
import { formatResponseRange, getCategoryIconUrl, isLightBg } from '@/utils/helpers';
const MERCHANT_TOP_N = 10;
const MIN_TILE_RATIO = 0.05;
export default /*@__PURE__*/ _defineComponent({
    __name: 'SpendingTreemapPage',
    props: {
        embedded: { type: Boolean },
        tileBorderColour: {},
        filterCategory: {},
        filterSubCategory: {}
    },
    setup(__props) {
        use([CanvasRenderer, TreemapChart]);
        // `embedded` hides the routed-page chrome (header, period toggle, hint) so this
        // page can be dropped into the desktop dashboard as a plain component.
        // `tileBorderColour` lets the host override the gap colour (ECharts can't read
        // CSS vars), e.g. to match the desktop dark card background.
        // `filterCategory` / `filterSubCategory` let a host (the desktop dashboard) drive
        // the treemap's drill from its own category filter — set the parent name to drill
        // into a category, or the sub-category name to drill to its merchants.
        const props = __props;
        const state = useAppState();
        const router = useRouter();
        useAuth();
        const { isDarkMode } = useDarkMode();
        // Tile gaps should read as the page background showing through — match
        // --background-color-main from App.vue (ECharts can't resolve CSS vars).
        const tileBorder = computed(() => props.tileBorderColour ?? (isDarkMode.value ? '#0f0f1e' : '#F9F7F3'));
        const { getBankingInfo } = useBanking();
        const { getAvailableVouchers } = useVouchers();
        const { updatingTransactionId, takePending } = useTransactionUpdateHandoff();
        const summary = ref(null);
        const loading = ref(true);
        const flatSubCategories = ref(false);
        const zoomedName = ref(null);
        const selectedChild = ref(null);
        const zoomedBucket = ref(null);
        const chartRef = useTemplateRef('chartRef');
        const chartEl = useTemplateRef('chartEl');
        const merchantDrawerOpen = ref(false);
        const selectedMerchantName = ref(null);
        const infoDrawerOpen = ref(false);
        // Authoritative parent-category colours from the classification tree, keyed by
        // category name — same source SpendingAnalysisPage uses, so the two pages agree.
        const categoryColours = ref(new Map());
        // Reactive — re-derives whenever summary updates (e.g. after a pollUntilChanged
        // response from a TDP category change lands), so the drawer's row classifications
        // refresh in place without us re-snapshotting.
        const drawerTransactions = computed(() => {
            const name = selectedMerchantName.value;
            if (!name)
                return [];
            const txs = merchantTransactions.value.get(name) ?? [];
            return txs.length ? groupTransactionsByDay(txs) : [];
        });
        function openMerchantDrawer(name) {
            if (!name)
                return;
            selectedMerchantName.value = name;
            // Defer opening to the next task so the originating click finishes
            // propagating before the overlay mounts. Otherwise the same click reaches
            // the freshly-teleported overlay and its @click.self handler closes it.
            setTimeout(() => {
                merchantDrawerOpen.value = true;
            }, 50);
        }
        function goToTransaction(t) {
            if (!t?.id)
                return;
            router.push('/TransactionDetail/' + t.id);
        }
        // Click from inside the merchant drawer — stash the active drill state in
        // sessionStorage so when the user navigates back from TDP we land on the same
        // view: same flat/nested mode, same zoom target, same merchant drawer open
        // (with the row spinner still showing if the change is in flight, via
        // inflightCategoryUpdate → updatingTransactionId).
        function onDrawerTransactionClick(t) {
            if (!t?.id)
                return;
            const snapshot = {
                merchant: selectedMerchantName.value,
                flatSubCategories: flatSubCategories.value,
                zoomedName: zoomedName.value,
                selectedChild: selectedChild.value,
                zoomedBucket: zoomedBucket.value,
            };
            sessionStorage.setItem('reopenTreemap', JSON.stringify(snapshot));
            goToTransaction(t);
        }
        // Merchant to reopen after the next load() completes — populated from the
        // sessionStorage snapshot in onMounted, cleared once openMerchantDrawer fires.
        const pendingReopenMerchant = ref(null);
        const analysisRange = computed(() => {
            const monthRange = getMonthRangeForSelection(dateSelection.value, selectedPeriod.value);
            if (monthRange) {
                const [fy, fm] = monthRange.startMonth.split('-').map(Number);
                const [ty, tm] = monthRange.endMonth.split('-').map(Number);
                return {
                    fromDate: new Date(Date.UTC(fy, fm - 1, 1)),
                    toDate: new Date(Date.UTC(ty, tm, 1)),
                };
            }
            return getDefaultMonthRange(snapToSpendingCycle.value);
        });
        const selectedAccountIds = computed(() => {
            const key = selectedAccount.value;
            const accounts = (state.bankInfo?.accounts || []).filter((a) => a.source === 0);
            if (!accounts.length || key === 'all')
                return undefined;
            const typeMap = { current: 'Current', credit: 'Credit', savings: 'Savings' };
            if (typeMap[key]) {
                const ids = accounts.filter((a) => a.accountType?.prettyName === typeMap[key]).map((a) => a.id);
                return ids.length ? ids : undefined;
            }
            const match = accounts.find((a) => a.id === key);
            return match ? [match.id] : undefined;
        });
        const periodOptions = [
            { key: 'month', label: 'Month' },
            { key: 'Year', label: 'Year' },
        ];
        function onPeriodSelect(key) {
            // Tapping the already-selected period would wipe the overlays without
            // triggering a reload — the analysisRange watcher wouldn't fire, so the
            // chart's 'finished' event never re-runs computeIconOverlays. Bail early.
            if (key === selectedPeriod.value)
                return;
            selectedPeriod.value = key;
            // Clearing any custom date selection so the period preset takes effect,
            // matching SpendingAnalysisPage's toggle behaviour.
            dateSelection.value = null;
            // Drop overlays so labels/icons don't linger over the old layout while the
            // new data is fetching and the chart re-tweens.
            iconOverlays.value = [];
            labelOverlays.value = [];
        }
        const baseTree = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            return parents
                .filter((p) => p.name !== 'Income' && (p.totalOut ?? 0) > 0)
                .map((p, i) => {
                const baseColor = categoryColours.value.get(p.name)
                    || p.categoryColor || p.color || SPENDING_BREAKDOWN_PALETTE[i % SPENDING_BREAKDOWN_PALETTE.length];
                const subMap = new Map();
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    if (t.amount >= 0)
                        continue;
                    const subName = t.subClassification ?? 'Other';
                    const row = subMap.get(subName) ?? { name: subName, value: 0, color: t.color ?? baseColor, iconPath: t.icon, merchants: new Map(), merchantIds: new Map() };
                    row.value += -t.amount;
                    const mName = t.merchantName || 'Unknown';
                    row.merchants.set(mName, (row.merchants.get(mName) ?? 0) + -t.amount);
                    // Capture the well-known merchant id so vouchers can match on it
                    // rather than the display name (which often differs, e.g. "Currys"
                    // vs the voucher's "Currys B2B").
                    if (t.merchantID)
                        row.merchantIds.set(mName, t.merchantID);
                    subMap.set(subName, row);
                }
                const children = [...subMap.values()]
                    .filter(c => c.value > 0)
                    .sort((a, b) => b.value - a.value)
                    .map(c => {
                    const merchants = [...c.merchants.entries()]
                        .map(([name, value]) => ({ name, value }))
                        .sort((a, b) => b.value - a.value);
                    // Split into "big enough to render" (top N AND ≥3% of the sub-cat
                    // total) vs the rest, which roll into a single "More" tile. The
                    // ratio threshold prevents top-10 entries that are still too tiny
                    // to fit a label.
                    const minValue = c.value * 0.03;
                    const top = [];
                    const tail = [];
                    merchants.forEach((m, i) => {
                        if (m.value <= 0)
                            return;
                        if (i < MERCHANT_TOP_N && m.value >= minValue)
                            top.push(m);
                        else
                            tail.push(m);
                    });
                    const merchantChildren = top.map(m => ({
                        name: m.name,
                        value: Math.round(m.value),
                        itemStyle: { color: c.color },
                        merchantId: c.merchantIds.get(m.name),
                    }));
                    if (tail.length) {
                        const otherValue = tail.reduce((s, m) => s + m.value, 0);
                        if (otherValue > 0) {
                            // Unique internal name so visibleSubtree's lookup can disambiguate
                            // More buckets across sub-categories — the visible label "More"
                            // is set when building the labelOverlay for the tile.
                            merchantChildren.push({
                                name: `${c.name} · More`,
                                value: Math.round(otherValue),
                                itemStyle: { color: c.color },
                                children: tail.map(m => ({
                                    name: m.name,
                                    value: Math.round(m.value),
                                    itemStyle: { color: c.color },
                                    merchantId: c.merchantIds.get(m.name),
                                })),
                            });
                        }
                    }
                    return {
                        name: c.name,
                        value: Math.round(c.value),
                        itemStyle: { color: c.color },
                        iconPath: c.iconPath,
                        children: merchantChildren.length ? merchantChildren : undefined,
                    };
                });
                return {
                    name: p.name,
                    value: Math.round(p.totalOut ?? 0),
                    itemStyle: { color: baseColor },
                    iconPath: p.name + '.svg',
                    children: children.length ? children : undefined,
                };
            })
                .sort((a, b) => b.value - a.value);
        });
        // Experimental flat view: drop the parent layer and surface every sub-category
        // at the top level, sorted by spend. Drill paths shorten by one (subcat →
        // merchant → More) — handled in onSliceClick / zoomBack via a synthetic path
        // shim so the rest of the logic stays unchanged.
        const treeData = computed(() => {
            if (!flatSubCategories.value)
                return baseTree.value;
            const flat = [];
            for (const p of baseTree.value) {
                for (const sub of p.children ?? [])
                    flat.push(sub);
            }
            return flat.sort((a, b) => b.value - a.value);
        });
        // Flat lookup of transactions by merchant name, sourced from the same
        // spending-analysis payload that feeds the treemap — so the drawer is always
        // consistent with what's on screen, regardless of date range.
        const merchantTransactions = computed(() => {
            const map = new Map();
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            for (const p of parents) {
                if (p.name === 'Income')
                    continue;
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    if (t.amount >= 0)
                        continue;
                    const name = t.merchantName || 'Unknown';
                    // Map the spending-analysis row shape into the one TransactionsList
                    // expects (effectiveClassification + timestampString).
                    const mapped = {
                        id: t.id,
                        merchantName: t.merchantName,
                        amount: t.amount,
                        timestamp: t.timestamp,
                        timestampString: formatTimestampString(t.timestamp),
                        wellKnownMerchant: t.wellKnownMerchant,
                        customerTags: t.tags ?? t.customerTags,
                        effectiveClassification: {
                            mgClassification: t.classification,
                            mgSubClassification: t.subClassification,
                            color: t.color ?? p.color,
                            iconPath: t.icon,
                        },
                    };
                    const list = map.get(name) ?? [];
                    list.push(mapped);
                    map.set(name, list);
                }
            }
            return map;
        });
        const totalSpend = computed(() => treeData.value.reduce((sum, n) => sum + n.value, 0));
        // Inflated copy of treeData fed to the chart so very small tiles get bumped
        // up to a minimum share of their siblings. Proportions become approximate but
        // every tile is big enough to host a label. Original amounts ride along on
        // `realValue` so the label still shows the true number.
        function inflateChildren(nodes) {
            const total = nodes.reduce((s, n) => s + n.value, 0);
            const floor = Math.round(total * MIN_TILE_RATIO);
            return nodes.map(n => ({
                name: n.name,
                value: Math.max(n.value, floor),
                realValue: n.value,
                itemStyle: n.itemStyle,
                iconPath: n.iconPath,
                children: n.children?.length ? inflateChildren(n.children) : undefined,
            }));
        }
        // The actual visible subtree, based on the current zoom refs. Rendering the
        // drilled-into subtree as the chart's natural top level (instead of dispatching
        // treemapRootToNode programmatically) sidesteps an ECharts quirk where tiles
        // come back uncoloured after a programmatic root change.
        const visibleSubtree = computed(() => {
            const tree = treeData.value;
            const findSubInTree = (subName) => {
                if (flatSubCategories.value) {
                    return tree.find(s => s.name === subName) ?? null;
                }
                // Scope to the drilled-into parent — generic sub names like 'Other'
                // (the null-subClassification fallback) are not unique across parents.
                if (zoomedName.value) {
                    const parent = tree.find(p => p.name === zoomedName.value);
                    return (parent?.children ?? []).find(c => c.name === subName) ?? null;
                }
                for (const p of tree) {
                    const sub = (p.children ?? []).find(c => c.name === subName);
                    if (sub)
                        return sub;
                }
                return null;
            };
            if (zoomedBucket.value && selectedChild.value) {
                const sub = findSubInTree(selectedChild.value);
                const bucket = (sub?.children ?? []).find(c => c.name === zoomedBucket.value);
                if (bucket?.children?.length)
                    return bucket.children;
            }
            if (selectedChild.value) {
                const sub = findSubInTree(selectedChild.value);
                if (sub?.children?.length)
                    return sub.children;
            }
            if (zoomedName.value && !flatSubCategories.value) {
                const parent = tree.find(p => p.name === zoomedName.value);
                if (parent?.children?.length)
                    return parent.children;
            }
            return tree;
        });
        const chartData = computed(() => inflateChildren(visibleSubtree.value));
        const rangeLabel = computed(() => {
            const r = summary.value;
            if (r?.fromDate && r?.toDate)
                return formatResponseRange(r.fromDate, r.toDate);
            return '';
        });
        // Amount shown in the meta header. Tracks the current drill level so the header
        // always reflects what's on screen.
        const visibleTotal = computed(() => {
            const findSub = () => {
                if (!selectedChild.value)
                    return null;
                if (flatSubCategories.value) {
                    return treeData.value.find(p => p.name === selectedChild.value) ?? null;
                }
                if (zoomedName.value) {
                    const parent = treeData.value.find(p => p.name === zoomedName.value);
                    return (parent?.children ?? []).find((c) => c.name === selectedChild.value) ?? null;
                }
                for (const p of treeData.value) {
                    const sub = (p.children ?? []).find((c) => c.name === selectedChild.value);
                    if (sub)
                        return sub;
                }
                return null;
            };
            if (zoomedBucket.value) {
                const sub = findSub();
                const bucket = (sub?.children ?? []).find((c) => c.name === zoomedBucket.value);
                if (bucket)
                    return bucket.value;
            }
            const sub = findSub();
            if (sub)
                return sub.value;
            if (zoomedName.value && !flatSubCategories.value) {
                const parent = treeData.value.find(p => p.name === zoomedName.value);
                if (parent)
                    return parent.value;
            }
            return totalSpend.value;
        });
        // Pill shown above the meta block when drilled: shows the deepest active level
        // (bucket → sub-cat → parent) with a coloured dot.
        const zoomCrumb = computed(() => {
            const findSub = () => {
                if (!selectedChild.value)
                    return null;
                if (flatSubCategories.value) {
                    return treeData.value.find(p => p.name === selectedChild.value) ?? null;
                }
                if (zoomedName.value) {
                    const parent = treeData.value.find(p => p.name === zoomedName.value);
                    return (parent?.children ?? []).find((c) => c.name === selectedChild.value) ?? null;
                }
                for (const p of treeData.value) {
                    const sub = (p.children ?? []).find((c) => c.name === selectedChild.value);
                    if (sub)
                        return sub;
                }
                return null;
            };
            if (zoomedBucket.value) {
                const sub = findSub();
                if (sub)
                    return { name: sub.name, color: sub.itemStyle?.color ?? '' };
            }
            const sub = findSub();
            if (sub)
                return { name: sub.name, color: sub.itemStyle?.color ?? '' };
            if (zoomedName.value && !flatSubCategories.value) {
                const parent = treeData.value.find(p => p.name === zoomedName.value);
                if (parent)
                    return { name: parent.name, color: parent.itemStyle?.color ?? '' };
            }
            return null;
        });
        const chartOption = computed(() => {
            if (!treeData.value.length)
                return null;
            return {
                series: [{
                        type: 'treemap',
                        data: chartData.value,
                        leafDepth: 1,
                        roam: false,
                        nodeClick: false,
                        drillDownIcon: '',
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        breadcrumb: { show: false },
                        // Labels are rendered as HTML overlays (see labelOverlays) so they
                        // can be cleared at the start of a zoom — toggling echarts' own
                        // labels via setOption resets the view root.
                        label: { show: false },
                        upperLabel: { show: false },
                        itemStyle: {
                            borderWidth: 2,
                            gapWidth: 2,
                            borderColor: tileBorder.value,
                        },
                        emphasis: {
                            disabled: true,
                        },
                        levels: [
                            {
                                itemStyle: { borderWidth: 0.5, gapWidth: 0, borderColor: tileBorder.value },
                            },
                            {
                                itemStyle: { borderWidth: 0.5, gapWidth: 0, borderColor: tileBorder.value },
                            },
                            {
                                colorSaturation: [0.4, 0.75],
                                itemStyle: { borderWidth: 0.5, gapWidth: 0, borderColor: tileBorder.value },
                            },
                            {
                                colorSaturation: [0.25, 0.6],
                                itemStyle: {
                                    borderWidth: 0.5,
                                    gapWidth: 0,
                                    borderColor: tileBorder.value,
                                },
                                upperLabel: { show: false },
                            },
                            {
                                colorSaturation: [0.2, 0.55],
                                itemStyle: {
                                    borderWidth: 0.5,
                                    gapWidth: 0,
                                    borderColor: tileBorder.value,
                                },
                                upperLabel: { show: false },
                                emphasis: {
                                    itemStyle: {
                                        borderWidth: 0,
                                        borderColor: 'transparent',
                                    },
                                },
                            },
                        ],
                    }],
            };
        });
        // IMPORTANT: do NOT replace this with `inst.dispatchAction({ type:
        // 'treemapRootToNode', ... })`. Programmatic root changes on this treemap
        // leave tiles drawn with no fill (an ECharts quirk we hit when restoring
        // drill state on back-navigation). We drive drill state purely via the zoom
        // refs — chartData re-derives from `visibleSubtree`, so the chart re-renders
        // the new layer naturally. Trade-off: no native zoom-in animation; tiles
        // cross-fade between drill levels instead.
        function onSliceClick(params) {
            const clicked = params?.data?.name;
            if (!clicked)
                return;
            const hasChildren = Array.isArray(params?.data?.children) && params.data.children.length > 0;
            // Overlays are positioned against the previous layout — clear them before
            // any drill so they don't sit in stale positions while tiles re-render.
            // The chart's 'finished' event repopulates them on the new layout.
            const clearOverlays = () => {
                iconOverlays.value = [];
                labelOverlays.value = [];
            };
            // Branch on the current drill level (deepest-first) — chartData is the
            // visible subtree, so a click always lands on one of its direct children.
            if (zoomedBucket.value) {
                // Inside a "More" bucket — clicked tile is a real merchant.
                openMerchantDrawer(clicked);
                return;
            }
            if (selectedChild.value) {
                // Merchant view — tile is either a real merchant or a "More" bucket.
                if (hasChildren) {
                    clearOverlays();
                    zoomedBucket.value = clicked;
                }
                else {
                    openMerchantDrawer(clicked);
                }
                return;
            }
            if (zoomedName.value || flatSubCategories.value) {
                // Sub-category view — drilled into a parent in nested mode, or flat mode
                // at the top level.
                clearOverlays();
                selectedChild.value = clicked;
                return;
            }
            // Top level (nested mode) — clicked tile is a parent.
            clearOverlays();
            zoomedName.value = clicked;
        }
        function toggleFlat(value) {
            flatSubCategories.value = value;
            zoomedName.value = null;
            selectedChild.value = null;
            zoomedBucket.value = null;
            iconOverlays.value = [];
            labelOverlays.value = [];
        }
        function zoomOut() {
            zoomedName.value = null;
            selectedChild.value = null;
            zoomedBucket.value = null;
        }
        function zoomBack() {
            // chartData is driven by the zoom refs, so clearing the deepest one is
            // enough — the chart re-renders one level out automatically.
            iconOverlays.value = [];
            labelOverlays.value = [];
            if (zoomedBucket.value) {
                zoomedBucket.value = null;
                return;
            }
            if (selectedChild.value) {
                selectedChild.value = null;
                return;
            }
            zoomedName.value = null;
        }
        // Drive the drill from the host's category filter (desktop dashboard). A
        // sub-category wins over a parent; clearing both returns to the full overview.
        function applyExternalFilter() {
            if (!props.embedded)
                return;
            zoomedBucket.value = null;
            iconOverlays.value = [];
            labelOverlays.value = [];
            if (props.filterSubCategory) {
                selectedChild.value = props.filterSubCategory;
                zoomedName.value = null;
            }
            else if (props.filterCategory) {
                zoomedName.value = props.filterCategory;
                selectedChild.value = null;
            }
            else {
                zoomedName.value = null;
                selectedChild.value = null;
            }
        }
        function load() {
            loading.value = true;
            const { fromDate, toDate } = analysisRange.value;
            // Also poll-until-changed when a TDP category update is still in flight —
            // the one-shot pendingWriteRevalidate flag may already have been consumed
            // by TDP's own fetchSummary, but the long-lived inflightCategoryUpdate
            // marker tells us the change hasn't landed yet.
            const pollUntilChanged = consumePendingWriteRevalidate() || !!inflightCategoryUpdate.value;
            getSpendingAnalysis(fromDate, toDate, {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            }, (result, isStale) => {
                if (result)
                    summary.value = result;
                // Restore the merchant drawer on first data tick after a back-from-TDP.
                // merchantTransactions reads off summary, so opening any earlier would
                // land an empty list. We don't gate on isStale — the cached snapshot
                // still has the txn rows; row classifications refresh in place when
                // the pollUntilChanged cycle resolves. The treemap drill is re-applied
                // separately via the chartData watcher.
                if (summary.value && pendingReopenMerchant.value) {
                    openMerchantDrawer(pendingReopenMerchant.value);
                    pendingReopenMerchant.value = null;
                }
                // While polling for a server-side write to land, the stale callback is
                // the pre-write snapshot — keep the loading state up until the fresh
                // payload arrives.
                if (!(pollUntilChanged && isStale)) {
                    loading.value = false;
                }
                // Only clear the drawer row spinner on the polling cycle's response —
                // a non-pollUntilChanged fetch may fire !isStale with the pre-write
                // snapshot, which would stop it before the new category lands.
                if (!isStale && pollUntilChanged) {
                    updatingTransactionId.value = null;
                }
            }, /* refreshCache */ false, /* staleWhileRevalidate */ true, pollUntilChanged);
        }
        const voucherByMerchant = computed(() => {
            const byName = new Map();
            const bySysoId = new Map();
            for (const v of state.availableVouchers ?? []) {
                if ((v.effectiveDiscountPercentage ?? 0) <= 0)
                    continue;
                const name = v?.merchant?.name;
                if (name)
                    byName.set(name.toLowerCase(), v);
                const sysoId = v?.merchant?.sysoMerchantId;
                if (sysoId)
                    bySysoId.set(sysoId, v);
            }
            return { byName, bySysoId };
        });
        // Merchants visible in the current view that also have an available retail-card
        // voucher. "Visible" = drilled into a sub-category (selectedChild set); we
        // flatten any "More" bucket so its inner merchants are considered too.
        const voucherMerchantsInView = computed(() => {
            const { byName, bySysoId } = voucherByMerchant.value;
            if (!selectedChild.value || (byName.size === 0 && bySysoId.size === 0))
                return [];
            const out = [];
            const seen = new Set();
            const collect = (nodes) => {
                for (const n of nodes) {
                    if (n.children?.length) {
                        collect(n.children);
                        continue;
                    }
                    const id = n.merchantId;
                    const key = (n.name ?? '').toLowerCase();
                    // Match by well-known merchant id first (reliable), then display name.
                    const dedupe = id || key;
                    if (!dedupe || seen.has(dedupe))
                        continue;
                    const v = (id && bySysoId.get(id)) || byName.get(key);
                    if (v) {
                        seen.add(dedupe);
                        out.push({ voucher: v, merchantName: n.name, spend: n.value });
                    }
                }
            };
            if (flatSubCategories.value) {
                const sub = treeData.value.find(p => p.name === selectedChild.value);
                if (sub)
                    collect(sub.children ?? []);
            }
            else {
                for (const p of treeData.value) {
                    const sub = (p.children ?? []).find((s) => s.name === selectedChild.value);
                    if (!sub)
                        continue;
                    collect(sub.children ?? []);
                    break;
                }
            }
            return out.sort((a, b) => b.spend - a.spend);
        });
        const iconOverlays = ref([]);
        const labelOverlays = ref([]);
        // Flat lookup of {iconPath, color} by node name, sourced from treeData. Names
        // are unique across the parent + sub-cat levels we render icons on.
        const iconByName = computed(() => {
            const m = new Map();
            for (const p of treeData.value) {
                const pColor = p.itemStyle?.color ?? '';
                if (p.iconPath)
                    m.set(p.name, { iconPath: p.iconPath, color: pColor });
                for (const c of p.children ?? []) {
                    if (c.iconPath)
                        m.set(c.name, { iconPath: c.iconPath, color: c.itemStyle?.color ?? pColor });
                }
            }
            return m;
        });
        function computeIconOverlays() {
            const inst = chartRef.value?.chart;
            if (!inst) {
                iconOverlays.value = [];
                labelOverlays.value = [];
                return;
            }
            const series = inst.getModel().getSeriesByIndex(0);
            const root = series?.getViewRoot?.();
            if (!root) {
                iconOverlays.value = [];
                labelOverlays.value = [];
                return;
            }
            const icons = [];
            const labels = [];
            const visit = (node, depth) => {
                if (!node)
                    return;
                const layout = node.getLayout?.();
                if (!layout)
                    return;
                const minSide = Math.min(layout.width, layout.height);
                // Icon — only for tiles big enough to host it without crowding, and
                // only when the tile wasn't inflated (real value matches the visual).
                const entry = iconByName.value.get(node.name);
                const visualValue = typeof node.getValue === 'function' ? Number(node.getValue()) : 0;
                const realValueForIcon = node.getModel?.().get?.('realValue');
                const wasInflated = typeof realValueForIcon === 'number' && realValueForIcon < visualValue;
                // Reserve enough room below the icon for the label (name row + pill)
                // so the two don't overlap. Hide the icon if the tile is too short.
                const size = Math.min(80, Math.round(minSide * 0.35));
                const labelReserve = 56;
                const hasRoomForIcon = layout.height >= size + labelReserve + 16;
                if (entry && minSide >= 56 && hasRoomForIcon && !wasInflated) {
                    icons.push({
                        key: `${depth}-${node.name}`,
                        url: getCategoryIconUrl(entry.iconPath),
                        cx: layout.x + layout.width / 2,
                        cy: layout.y + (layout.height - labelReserve) / 2,
                        size,
                        light: isLightBg(entry.color ?? ''),
                        topLevel: depth === 0,
                    });
                }
                // Label — HTML overlay so it can be cleared during a zoom (echarts'
                // own labels can't be toggled mid-zoom without resetting the view).
                if (layout.width >= 32 && layout.height >= 22) {
                    const fill = node.getVisual?.('style')?.fill
                        || node.getModel?.().get?.('itemStyle.color')
                        || entry?.color
                        || '';
                    const isBucket = typeof node.name === 'string' && node.name.endsWith(' · More');
                    const realValue = node.getModel?.().get?.('realValue');
                    const value = typeof realValue === 'number'
                        ? realValue
                        : (typeof node.getValue === 'function' ? Number(node.getValue()) : 0);
                    const small = minSide < 64;
                    // Hide the value if the tile can't comfortably fit both name + value
                    // lines, OR if it's too narrow to render a formatted amount without
                    // ellipsing to something useless like "£1…".
                    const minHeightForValue = small ? 36 : 44;
                    const minWidthForValue = small ? 56 : 64;
                    const showValue = !isBucket
                        && layout.height >= minHeightForValue
                        && layout.width >= minWidthForValue;
                    labels.push({
                        key: `${depth}-${node.name}`,
                        name: isBucket ? 'More' : node.name,
                        value: showValue ? state.formatter.format(value) : '',
                        x: layout.x,
                        y: layout.y,
                        w: layout.width,
                        h: layout.height,
                        light: isLightBg(fill),
                        small,
                        color: fill,
                    });
                }
                // Only descend one level — matches leafDepth: 1, the visible leaves.
                if (depth < 1 && node.children?.length) {
                    for (const c of node.children)
                        visit(c, depth + 1);
                }
            };
            if (root.children?.length) {
                for (const c of root.children)
                    visit(c, 0);
            }
            iconOverlays.value = icons;
            labelOverlays.value = labels;
        }
        function onVoucherClick(voucher) {
            if (voucher?.id)
                router.push('/vouchers/amount/' + voucher.id);
            else
                router.push('/shopvouchers');
        }
        watch(chartRef, (inst) => {
            const chart = inst?.chart;
            if (!chart)
                return;
            chart.on('finished', computeIconOverlays);
        });
        // No programmatic zoom restore needed — chartData is now driven directly by
        // the zoom refs (zoomedName / selectedChild / zoomedBucket). When onMounted
        // rehydrates those refs from sessionStorage, the chart naturally renders the
        // drilled subtree as its top-level view.
        // Clear overlays as soon as the data changes; the chart's 'finished' event
        // will repopulate them once the new layout has settled. Recomputing here
        // would paint labels against the old (animating) layout.
        watch(treeData, () => {
            iconOverlays.value = [];
            labelOverlays.value = [];
        });
        onMounted(() => {
            // Adopt any in-flight category update from TDP so the affected row spins
            // if the user happens to open the merchant drawer for that merchant.
            // Falls back from pendingTransactionUpdateId to inflightCategoryUpdate, so
            // it works on both the first landing here and any subsequent bounce-back.
            takePending();
            // Restore drill state + merchant drawer after a back-from-TDP. Stashed by
            // onDrawerTransactionClick; we replay the simple refs synchronously and
            // defer the actual chart zoom + drawer open until the data has loaded.
            const raw = sessionStorage.getItem('reopenTreemap');
            if (raw) {
                sessionStorage.removeItem('reopenTreemap');
                try {
                    const s = JSON.parse(raw);
                    flatSubCategories.value = !!s.flatSubCategories;
                    zoomedName.value = s.zoomedName ?? null;
                    selectedChild.value = s.selectedChild ?? null;
                    zoomedBucket.value = s.zoomedBucket ?? null;
                    pendingReopenMerchant.value = s.merchant ?? null;
                }
                catch { /* ignore malformed snapshot */ }
            }
            getAvailableVouchers();
            getTransactionClassificationTree((tree) => {
                if (!tree?.parents)
                    return;
                const m = new Map();
                for (const p of tree.parents) {
                    const c = p.categoryColor ?? p.parent?.categoryColor ?? p.color ?? p.parent?.color;
                    if (c)
                        m.set(p.name, c);
                }
                categoryColours.value = m;
            });
            if (state.bankInfo) {
                load();
            }
            else {
                getBankingInfo(() => load());
            }
        });
        // Watch a stable string key — analysisRange returns a fresh object on every
        // dep change (e.g. state.bankInfo updating from a banking poll), which would
        // otherwise re-fire load() for the same date range and spam SA fetches.
        watch(() => {
            const r = analysisRange.value;
            return `${r.fromDate?.toISOString() ?? ''}|${r.toDate?.toISOString() ?? ''}`;
        }, () => {
            zoomedName.value = null;
            selectedChild.value = null;
            zoomedBucket.value = null;
            // Keep the host's category drill applied across a date/account change.
            applyExternalFilter();
            load();
        });
        // Drive the drill from the host's category filter. Registered here (after all
        // refs exist) and immediate so the initial filter is applied on mount.
        watch(() => [props.filterCategory, props.filterSubCategory], applyExternalFilter, { immediate: true });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (!_ctx.embedded)
                    ? (_openBlock(), _createBlock(HeaderBlock, {
                        key: 0,
                        title: "Where does my money go?",
                        info: true,
                        onInfo: _cache[0] || (_cache[0] = ($event) => (infoDrawerOpen.value = true))
                    }))
                    : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_1, [
                    (!zoomCrumb.value && !_ctx.embedded)
                        ? (_openBlock(), _createBlock(PeriodToggle, {
                            key: 0,
                            class: "stm-period",
                            periods: periodOptions,
                            modelValue: _unref(selectedPeriod),
                            "onUpdate:modelValue": onPeriodSelect
                        }, null, 8, ["modelValue"]))
                        : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_2, [
                        (zoomCrumb.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                _createElementVNode("button", {
                                    class: "stm-crumb-back",
                                    onClick: zoomBack,
                                    "aria-label": "Back"
                                }, _cache[4] || (_cache[4] = [
                                    _createElementVNode("img", {
                                        src: _imports_0,
                                        alt: ""
                                    }, null, -1)
                                ])),
                                _createElementVNode("span", _hoisted_4, [
                                    _createElementVNode("span", {
                                        class: "stm-crumb-dot",
                                        style: _normalizeStyle({ backgroundColor: zoomCrumb.value.color })
                                    }, null, 4),
                                    _createElementVNode("span", _hoisted_5, _toDisplayString(zoomCrumb.value.name), 1)
                                ])
                            ]))
                            : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_6, _toDisplayString(rangeLabel.value), 1),
                        _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(state).formatter.format(visibleTotal.value)), 1)
                    ]),
                    _createElementVNode("label", {
                        class: _normalizeClass(["stm-toggle", { 'stm-toggle--hidden': zoomCrumb.value }])
                    }, [
                        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "stm-toggle-label" }, "Show all categories", -1)),
                        _createVNode(MGToggle, {
                            size: "small",
                            modelValue: flatSubCategories.value,
                            "onUpdate:modelValue": toggleFlat
                        }, null, 8, ["modelValue"])
                    ], 2),
                    _createElementVNode("div", {
                        class: "stm-chart",
                        ref_key: "chartEl",
                        ref: chartEl
                    }, [
                        (chartOption.value)
                            ? (_openBlock(), _createBlock(_unref(VChart), {
                                key: 0,
                                ref_key: "chartRef",
                                ref: chartRef,
                                option: chartOption.value,
                                autoresize: "",
                                class: "stm-chart-canvas",
                                onClick: onSliceClick
                            }, null, 8, ["option"]))
                            : _createCommentVNode("", true),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(labelOverlays.value, (l) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: l.key,
                                class: _normalizeClass(["stm-tile-label", { 'stm-tile-label--dark': l.light, 'stm-tile-label--small': l.small }]),
                                style: _normalizeStyle({
                                    left: l.x + 'px',
                                    top: l.y + 'px',
                                    width: l.w + 'px',
                                    height: l.h + 'px',
                                })
                            }, [
                                _createElementVNode("div", _hoisted_8, [
                                    _cache[6] || (_cache[6] = _createElementVNode("span", { class: "stm-tile-label-dot" }, null, -1)),
                                    _createElementVNode("span", _hoisted_9, _toDisplayString(l.name), 1)
                                ]),
                                (l.value)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(l.value), 1))
                                    : _createCommentVNode("", true)
                            ], 6));
                        }), 128)),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(iconOverlays.value, (o) => {
                            return (_openBlock(), _createElementBlock("img", {
                                key: o.key,
                                src: o.url,
                                class: _normalizeClass(["stm-tile-icon", { 'stm-tile-icon--dark': !o.topLevel && o.light, 'stm-tile-icon--top': o.topLevel }]),
                                style: _normalizeStyle({
                                    left: (o.cx - o.size / 2) + 'px',
                                    top: (o.cy - o.size / 2) + 'px',
                                    width: o.size + 'px',
                                    height: o.size + 'px',
                                }),
                                alt: ""
                            }, null, 14, _hoisted_11));
                        }), 128)),
                        (!chartOption.value && loading.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, "Loading…"))
                            : (!chartOption.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, "No spending for this period"))
                                : _createCommentVNode("", true)
                    ], 512),
                    (chartOption.value && !_ctx.embedded)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_14, _cache[7] || (_cache[7] = [
                            _createTextVNode(" Tap a category to drill in. Tap "),
                            _createElementVNode("strong", null, "More", -1),
                            _createTextVNode(" to see smaller merchants. Use the back arrow above to zoom out. ")
                        ])))
                        : _createCommentVNode("", true),
                    _createVNode(_Transition, { name: "stm-vouchers-fade" }, {
                        default: _withCtx(() => [
                            (voucherMerchantsInView.value.length > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(voucherMerchantsInView.value, (m) => {
                                        return (_openBlock(), _createBlock(RetailDiscountCard, {
                                            key: m.merchantName,
                                            voucher: m.voucher,
                                            merchantName: m.merchantName,
                                            onClick: ($event) => (onVoucherClick(m.voucher))
                                        }, {
                                            default: _withCtx(() => [
                                                _cache[8] || (_cache[8] = _createTextVNode(" You’ve spent ")),
                                                _createElementVNode("strong", null, _toDisplayString(_unref(state).formatter.format(m.spend)), 1),
                                                _createTextVNode(" at " + _toDisplayString(m.merchantName) + " in " + _toDisplayString(rangeLabel.value) + ". You could have saved up to ", 1),
                                                _createElementVNode("strong", null, _toDisplayString(_unref(state).formatter.format(m.spend * m.voucher.effectiveDiscountPercentage / 100)), 1),
                                                _cache[9] || (_cache[9] = _createTextVNode(" with Retail Cards. "))
                                            ]),
                                            _: 2
                                        }, 1032, ["voucher", "merchantName", "onClick"]));
                                    }), 128))
                                ]))
                                : _createCommentVNode("", true)
                        ]),
                        _: 1
                    })
                ]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: merchantDrawerOpen.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((merchantDrawerOpen).value = $event)),
                    title: selectedMerchantName.value || 'Transactions',
                    height: "90vh"
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_16, [
                            (drawerTransactions.value.length)
                                ? (_openBlock(), _createBlock(TransactionsList, {
                                    key: 0,
                                    search: false,
                                    transactions: drawerTransactions.value,
                                    formatter: _unref(state).formatter,
                                    today: _unref(state).today,
                                    yesterday: _unref(state).yesterday,
                                    categoryPills: true,
                                    updatingTransactionId: _unref(updatingTransactionId),
                                    onTransactionClick: onDrawerTransactionClick
                                }, null, 8, ["transactions", "formatter", "today", "yesterday", "updatingTransactionId"]))
                                : _createCommentVNode("", true)
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: infoDrawerOpen.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((infoDrawerOpen).value = $event)),
                    title: "How this is worked out",
                    height: "auto",
                    closeButton: true,
                    divider: false
                }, {
                    footer: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: "Close",
                            fullWidth: true,
                            onClick: _cache[2] || (_cache[2] = ($event) => (infoDrawerOpen.value = false))
                        })
                    ]),
                    default: _withCtx(() => [
                        _cache[10] || (_cache[10] = _createElementVNode("div", { class: "stm-info" }, [
                            _createElementVNode("p", null, [
                                _createTextVNode("Each tile shows your "),
                                _createElementVNode("strong", null, "total spend"),
                                _createTextVNode(" — the money that went out of a category over this period.")
                            ]),
                            _createElementVNode("p", null, "Tiles are sized proportionally to their share of the total spend, and coloured according to your classification."),
                            _createElementVNode("p", null, "Tap a category to drill into its sub-categories and merchants.")
                        ], -1))
                    ]),
                    _: 1
                }, 8, ["modelValue"])
            ], 64));
        };
    }
});
